import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import {
  ArrowLeft,
  Plus,
  Save,
  ChevronDown,
  Upload,
  Download,
  FileText,
  X,
  Info,
  Image as ImageIcon
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'
import * as XLSX from 'xlsx'

const AddTestQuestions = () => {
  const navigate = useNavigate()
  const { testId, contentId } = useParams()
  const fileInputRef = React.useRef(null)

  // Get URL parameters
  const urlParams = new URLSearchParams(window.location.search)
  const testNameParam = urlParams.get('testName')
  const fromParam = urlParams.get('from')
  const isFromOnline = fromParam === 'online'

  // Determine if we're working with online tests or home content
  const isHomeContent = !!contentId && !isFromOnline
  const currentId = testId || contentId

  const [test, setTest] = useState(null)
  const [questions, setQuestions] = useState([])
  const [testQuestions, setTestQuestions] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [showQuestionForm, setShowQuestionForm] = useState(false)
  const [questionType, setQuestionType] = useState('text') // 'text' or 'image'
  const [questionImage, setQuestionImage] = useState(null)
  const [questionImagePreview, setQuestionImagePreview] = useState(null)
  const questionImageInputRef = React.useRef(null)
  const [questionForm, setQuestionForm] = useState({
    question: '',
    options: ['', '', '', ''],
    correctAnswer: 0,
    explanation: ''
  })

  const { getOnlineTests, getQuestions, getCategories, updateTestQuestions, addQuestion, addOnlineTestQuestions, getOnlineTestQuestions } = useFirebase()

  useEffect(() => {
    loadData()
    // Auto-open the question form when coming from online tests
    if (isFromOnline) {
      setShowQuestionForm(true)
    }
  }, [testId, contentId, isFromOnline])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.relative')) {
        setDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [dropdownOpen])

  const loadData = async () => {
    try {
      setLoading(true)

      if (isFromOnline) {
        // For online tests, load the test data from online tests collection
        const [testsData, questionsData, categoriesData] = await Promise.all([
          getOnlineTests(),
          getQuestions(),
          getCategories()
        ])

        const currentTest = testsData.find(t => t.id === testId)
        if (!currentTest) {
          toast.error('Online test not found')
          navigate('/home?tab=online')
          return
        }

        setTest(currentTest)
        setQuestions(questionsData || [])
        setCategories(categoriesData || [])

        // Load existing questions for this test
        const existingTestQuestions = await getOnlineTestQuestions(testId)
        setTestQuestions(existingTestQuestions || [])

      } else if (isHomeContent) {
        // For home content, we're creating a new test, so just load questions and categories
        const [questionsData, categoriesData] = await Promise.all([
          getQuestions(),
          getCategories()
        ])

        // Create a mock test object for home content
        setTest({
          id: contentId,
          title: 'New Test',
          description: 'Create questions for home content'
        })
        setQuestions(questionsData || [])
        setCategories(categoriesData || [])
      } else {
        // Fallback: try to load from online tests
        const [testsData, questionsData, categoriesData] = await Promise.all([
          getOnlineTests(),
          getQuestions(),
          getCategories()
        ])

        const currentTest = testsData.find(t => t.id === testId)
        if (!currentTest) {
          // If test name is provided in URL, create a mock test object
          if (testNameParam) {
            setTest({
              id: testId,
              testName: testNameParam,
              title: testNameParam,
              startDate: new Date(),
              endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
              duration: 60
            })
          } else {
            toast.error('Test not found')
            navigate('/home?tab=online')
            return
          }
        } else {
          setTest(currentTest)
        }

        setQuestions(questionsData || [])
        setCategories(categoriesData || [])
      }
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Failed to load data')
    } finally {
      setLoading(false)
    }
  }


  const handleQuestionFormSubmit = async (e) => {
    e.preventDefault()
    try {
      setSaving(true)

      if (isFromOnline) {
        // Validate image question has an image
        if (questionType === 'image' && !questionImage) {
          toast.error('Please upload an image for image question type')
          setSaving(false)
          return
        }

        // For online tests, use the new addOnlineTestQuestions function
        const questionData = [{
          question: questionForm.question,
          questionName: questionForm.question, // Use question text as questionName
          options: questionForm.options,
          correctAnswer: questionForm.correctAnswer,
          explanation: questionForm.explanation,
          questionType: questionType, // 'text' or 'image'
          questionImage: questionImage // File object for image upload
        }]

        const questionIds = await addOnlineTestQuestions(testId, questionData)

        toast.success('Question added to online test successfully!')

        // Reload test questions to show the newly added question
        const updatedTestQuestions = await getOnlineTestQuestions(testId)
        setTestQuestions(updatedTestQuestions || [])

        // Reset form but keep it open for adding more questions
        setQuestionForm({
          question: '',
          options: ['', '', '', ''],
          correctAnswer: 0,
          explanation: ''
        })
        setQuestionType('text')
        setQuestionImage(null)
        setQuestionImagePreview(null)

        // Keep the form open for continuous question adding
        setShowQuestionForm(true)

      } else {
        // For regular tests, use the existing logic
        const questionData = {
          ...questionForm,
          testId: testId,
          createdAt: new Date()
        }

        const newQuestionId = await addQuestion(questionData)

        // Create the question object with the new ID
        const newQuestion = { id: newQuestionId, ...questionData }

        // Add to local questions list
        setQuestions(prev => [newQuestion, ...prev])

        // Reset form but keep it open for adding more questions
        setQuestionForm({
          question: '',
          options: ['', '', '', ''],
          correctAnswer: 0,
          explanation: ''
        })
        setQuestionType('text')
        setQuestionImage(null)
        setQuestionImagePreview(null)

        // Keep the form open for continuous question adding
        setShowQuestionForm(true)

        toast.success('Question added and selected for test!')
      }

    } catch (error) {
      console.error('Error adding question:', error)
      toast.error('Failed to add question')
    } finally {
      setSaving(false)
    }
  }

  const handleOptionChange = (index, value) => {
    const newOptions = [...questionForm.options]
    newOptions[index] = value
    setQuestionForm(prev => ({ ...prev, options: newOptions }))
  }

  const handleQuestionImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image file')
        return
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB')
        return
      }

      setQuestionImage(file)

      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setQuestionImagePreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const removeQuestionImage = () => {
    setQuestionImage(null)
    setQuestionImagePreview(null)
    if (questionImageInputRef.current) {
      questionImageInputRef.current.value = ''
    }
  }

  const handleExcelUpload = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    // Check if file is Excel format
    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ]

    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/i)) {
      toast.error('Please select a valid Excel file (.xlsx or .xls)')
      return
    }

    try {
      setSaving(true)
      toast('Processing Excel file... This may take a moment.', {
        icon: 'ℹ️',
        duration: 4000
      })

      // Create FileReader to read the file
      const reader = new FileReader()

      reader.onload = async (e) => {
        try {
          // Parse Excel file using XLSX library
          const data = new Uint8Array(e.target.result)
          const workbook = XLSX.read(data, { type: 'array' })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 })

          // Parse the Excel data
          const parsedQuestions = parseQuestionsFromExcel(jsonData)

          if (parsedQuestions.length > 0) {
            if (isFromOnline) {
              // For online tests, add questions using addOnlineTestQuestions
              await addOnlineTestQuestions(testId, parsedQuestions)
              toast.success(`Successfully imported ${parsedQuestions.length} questions from Excel to online test!`)

              // Reload test questions to show the imported questions
              const updatedTestQuestions = await getOnlineTestQuestions(testId)
              setTestQuestions(updatedTestQuestions || [])
              // Close manual form after batch import to keep UI focused on imported questions
              setShowQuestionForm(false)
            } else {
              // For regular tests, add questions individually
              for (const questionData of parsedQuestions) {
                const newQuestionData = {
                  ...questionData,
                  testId: testId,
                  createdAt: new Date()
                }
                await addQuestion(newQuestionData)
              }
              toast.success(`Successfully imported ${parsedQuestions.length} questions from Excel!`)
              // Close manual form after batch import to keep UI clean
              setShowQuestionForm(false)
            }
          } else {
            toast.error('No valid questions found in the Excel file')
          }

          // Reset file input
          event.target.value = ''

        } catch (error) {
          console.error('Error processing Excel file:', error)
          toast.error('Failed to process Excel file. Please check the format.')
        } finally {
          setSaving(false)
        }
      }

      reader.onerror = () => {
        toast.error('Failed to read Excel file')
        setSaving(false)
      }

      // Read file as array buffer
      reader.readAsArrayBuffer(file)

    } catch (error) {
      console.error('Error uploading Excel file:', error)
      toast.error('Failed to upload Excel file')
      setSaving(false)
    }
  }

  const parseQuestionsFromExcel = (data) => {
    const parsed = []
    // Skip header row (index 0) and start from row 1
    for (let i = 1; i < data.length; i++) {
      const row = data[i]
      if (!row || row.length < 5) continue
      const question = row[0]?.toString().trim()
      const optionA = row[1]?.toString().trim()
      const optionB = row[2]?.toString().trim()
      const optionC = row[3]?.toString().trim()
      const optionD = row[4]?.toString().trim()
      const correctRaw = row[5]
      const explanation = row[6]?.toString().trim() || ''

      if (question && optionA && optionB && optionC && optionD) {
        const options = [optionA, optionB, optionC, optionD]
        const normalize = (s) => (s || '')
          .toString()
          .normalize('NFKD')
          .replace(/\u00A0/g, ' ')
          .replace(/\s+/g, ' ')
          .replace(/[^\p{L}\p{N}\s]/gu, '')
          .trim()
          .toLowerCase()

        const valRaw = (correctRaw ?? '').toString()
        const val = valRaw.trim()
        const upper = val.toUpperCase()
        let idx = -1

        // 1) Letter forms A-D
        if (['A', 'B', 'C', 'D'].includes(upper)) idx = 'ABCD'.indexOf(upper)
        // 2) Labeled: Option A-D or Answer: Option B
        if (idx < 0) {
          const m = upper.match(/^(?:ANSWER\s*[:\-]?\s*)?OPTION\s+([ABCD])$/)
          if (m) idx = 'ABCD'.indexOf(m[1])
        }
        // 3) Numeric 1-4 or 0-3
        if (idx < 0 && /^[1-4]$/.test(upper)) idx = parseInt(upper, 10) - 1
        if (idx < 0 && /^[0-3]$/.test(upper)) idx = parseInt(upper, 10)
        // 4) Exact text match (trim/case-insensitive)
        if (idx < 0) {
          const exactIdx = options.findIndex(o => (o || '').toString().trim().toLowerCase() === val.toLowerCase())
          if (exactIdx >= 0) idx = exactIdx
        }
        // 5) Normalized text match
        if (idx < 0) {
          const normVal = normalize(valRaw)
          const normIdx = options.findIndex(o => normalize(o) === normVal)
          if (normIdx >= 0) idx = normIdx
        }
        // 6) Fuzzy contains/startsWith on normalized strings
        if (idx < 0) {
          const normVal = normalize(valRaw)
          const containsIdx = options.findIndex(o => normalize(o).includes(normVal) || normVal.includes(normalize(o)))
          if (containsIdx >= 0) idx = containsIdx
        }
        // Default to A
        if (idx < 0) idx = 0
        idx = Math.max(0, Math.min(3, idx))

        const result = {
          question,
          questionName: question,
          options,
          correctAnswer: idx,
          explanation
        }
        try { console.log('[ExcelImport:Online] Row', i + 1, 'question:', question, 'correctRaw:', correctRaw, 'mappedIndex:', idx, 'mappedOption:', options[idx]) } catch { }
        parsed.push(result)
      }
    }
    return parsed
  }

  const downloadExcelTemplate = () => {
    // Create sample data for the template
    const templateData = [
      ['Question', 'Option A', 'Option B', 'Option C', 'Option D', 'Correct Answer (0-3)', 'Explanation'],
      [
        'What is the normal heart rate for adults?',
        '40-60 bpm',
        '60-100 bpm',
        '100-120 bpm',
        '120-140 bpm',
        1,
        'Normal adult heart rate ranges from 60-100 beats per minute at rest.'
      ],
      [
        'Which of the following is a sign of dehydration?',
        'Increased urination',
        'Decreased skin turgor',
        'Bradycardia',
        'Hypertension',
        1,
        'Decreased skin turgor (skin tenting) is a classic sign of dehydration.'
      ]
    ]

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.aoa_to_sheet(templateData)

    // Set column widths
    ws['!cols'] = [
      { wch: 50 }, // Question
      { wch: 20 }, // Option A
      { wch: 20 }, // Option B
      { wch: 20 }, // Option C
      { wch: 20 }, // Option D
      { wch: 18 }, // Correct Answer
      { wch: 60 }  // Explanation
    ]

    XLSX.utils.book_append_sheet(wb, ws, 'Questions Template')

    // Download the file
    XLSX.writeFile(wb, 'nursing_questions_template.xlsx')
    toast.success('Excel template downloaded successfully!')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  if (!test) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Test not found</h3>
        <button onClick={() => navigate(isHomeContent ? `/home-content/tests/${contentId}` : '/online-tests')} className="btn-primary">
          Back to Tests
        </button>
      </div>
    )
  }


  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(isFromOnline ? '/home?tab=online' : (isHomeContent ? `/home-content/tests/${contentId}` : '/home'))}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-6 w-6 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1">Add Test Questions</h1>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">For: {test?.testName || test?.title || 'Unknown Test'}</span>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Manual Entry</span>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Question
                <ChevronDown className="h-4 w-4 ml-2" />
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  <button
                    className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 w-full text-left"
                    onClick={() => {
                      setDropdownOpen(false)
                      setShowQuestionForm(true)
                    }}
                  >
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                      <Plus className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">Add New Question</div>
                      <div className="text-gray-500 text-xs">Add another question manually</div>
                    </div>
                  </button>

                  <button
                    className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 w-full text-left"
                    onClick={() => {
                      setDropdownOpen(false)
                      handleExcelUpload()
                    }}
                  >
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                      <Upload className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">Excel Upload</div>
                      <div className="text-gray-500 text-xs">Upload questions from Excel file</div>
                    </div>
                  </button>

                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Test Information Section */}
      <div className="bg-purple-50 rounded-2xl border border-purple-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
            <FileText className="h-4 w-4 text-purple-600" />
          </div>
          <h2 className="text-lg font-semibold text-purple-900">Test Information</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Test Name</label>
            <input
              type="text"
              value={test?.testName || test?.title || ''}
              readOnly
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="text"
              value={test?.startDate ? new Date(test.startDate.toDate ? test.startDate.toDate() : test.startDate).toLocaleDateString() : 'Not set'}
              readOnly
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
            <input
              type="text"
              value={test?.testStartTime || 'Not set'}
              readOnly
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="text"
              value={test?.endDate ? new Date(test.endDate.toDate ? test.endDate.toDate() : test.endDate).toLocaleDateString() : 'Not set'}
              readOnly
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Close Time</label>
            <input
              type="text"
              value={test?.testCloseTime || 'Not set'}
              readOnly
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Duration (min)</label>
            <input
              type="text"
              value={test?.duration || '60'}
              readOnly
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900"
            />
          </div>
        </div>
      </div>

      {/* Hidden File Input for Excel Upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* Add Question Form */}
      {showQuestionForm && (
        <div className="bg-purple-50 rounded-2xl border border-purple-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <Info className="h-4 w-4 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-purple-900">Question {testQuestions.length + 1}</h3>
            </div>
            <button
              onClick={() => setShowQuestionForm(false)}
              className="text-gray-400 hover:text-red-500 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleQuestionFormSubmit} className="space-y-6">
            {/* Question Type Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Question Type <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-4">
                {/* Text Question */}
                <button
                  type="button"
                  onClick={() => setQuestionType('text')}
                  className={`flex items-center justify-center space-x-2 p-4 border-2 rounded-lg transition-all ${questionType === 'text'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    }`}
                >
                  <FileText className="h-5 w-5" />
                  <span className="font-medium">Text Question</span>
                </button>

                {/* Image Question */}
                <button
                  type="button"
                  onClick={() => setQuestionType('image')}
                  className={`flex items-center justify-center space-x-2 p-4 border-2 rounded-lg transition-all ${questionType === 'image'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    }`}
                >
                  <ImageIcon className="h-5 w-5" />
                  <span className="font-medium">Image Question</span>
                </button>
              </div>
            </div>

            {/* Question Image Upload (only for image type) */}
            {questionType === 'image' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Question Image <span className="text-red-500">*</span>
                </label>
                {questionImagePreview ? (
                  <div className="relative">
                    <img
                      src={questionImagePreview}
                      alt="Question preview"
                      className="w-full max-h-64 object-contain rounded-lg border-2 border-gray-300"
                    />
                    <button
                      type="button"
                      onClick={removeQuestionImage}
                      className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => questionImageInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all"
                  >
                    <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm text-gray-600 mb-1">Click to upload question image</p>
                    <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                  </div>
                )}
                <input
                  ref={questionImageInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleQuestionImageChange}
                  className="hidden"
                />
              </div>
            )}

            {/* Question Text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Question Text <span className="text-red-500">*</span>
              </label>
              <textarea
                required
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                placeholder="Enter your question here..."
                value={questionForm.question}
                onChange={(e) => setQuestionForm(prev => ({ ...prev, question: e.target.value }))}
              />
            </div>

            {/* Answer Options */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Answer Options <span className="text-red-500">*</span>
              </label>
              <div className="space-y-3">
                {questionForm.options.map((option, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="correctAnswer"
                        checked={questionForm.correctAnswer === index}
                        onChange={() => setQuestionForm(prev => ({ ...prev, correctAnswer: index }))}
                        className="w-4 h-4 text-green-600 focus:ring-green-500 border-gray-300"
                      />
                    </div>
                    <input
                      type="text"
                      required
                      className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={`Option ${String.fromCharCode(65 + index)}`}
                      value={option}
                      onChange={(e) => handleOptionChange(index, e.target.value)}
                    />
                    <span className="w-8 text-center text-sm font-medium text-gray-500 bg-gray-100 rounded px-2 py-1">
                      {String.fromCharCode(65 + index)}
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Click the circle next to an option to mark it as the correct answer
              </p>
            </div>

            {/* Explanation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Explanation (Optional)
              </label>
              <textarea
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                placeholder="Explain why this is the correct answer..."
                value={questionForm.explanation}
                onChange={(e) => setQuestionForm(prev => ({ ...prev, explanation: e.target.value }))}
              />
            </div>


            {/* Form Actions */}
            <div className="flex items-center justify-between pt-4 border-t">
              <button
                type="button"
                onClick={() => setShowQuestionForm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setQuestionForm({
                      question: '',
                      options: ['', '', '', ''],
                      correctAnswer: 0,
                      explanation: ''
                    })
                    setQuestionType('text')
                    setQuestionImage(null)
                    setQuestionImagePreview(null)
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Reset
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary"
                >
                  {saving ? (
                    <>
                      <LoadingSpinner size="small" />
                      <span className="ml-2">Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save & Add Another
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Questions List Section */}
      {testQuestions.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <FileText className="h-4 w-4 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Added Questions ({testQuestions.length})</h2>
            </div>
          </div>

          <div className="space-y-6">
            {testQuestions.map((question, index) => (
              <div key={question.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-blue-600">{index + 1}</span>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Question {index + 1}</h3>
                      <p className="text-sm text-gray-500">ID: {question.id}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {question.questionType === 'image' && (
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700 font-medium flex items-center gap-1">
                        <ImageIcon className="w-3 h-3" />
                        Image
                      </span>
                    )}
                    <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700 font-medium">
                      {question.marks || 0} marks
                    </span>
                    <span className={`px-2 py-1 text-xs rounded-full ${question.optionA ? 'bg-green-100 text-green-700' :
                        question.optionB ? 'bg-blue-100 text-blue-700' :
                          question.optionC ? 'bg-purple-100 text-purple-700' :
                            'bg-orange-100 text-orange-700'
                      }`}>
                      Correct: {question.optionA ? 'A' : question.optionB ? 'B' : question.optionC ? 'C' : 'D'}
                    </span>
                  </div>
                </div>

                {/* Question Image (if image type) */}
                {question.questionType === 'image' && question.questionImageURL && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Question Image:</h4>
                    <img
                      src={question.questionImageURL}
                      alt={`Question ${index + 1}`}
                      className="w-full max-w-2xl max-h-96 object-contain rounded-lg border-2 border-gray-300"
                    />
                  </div>
                )}

                {/* Question Text */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Question:</h4>
                  <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">
                    {question.questionName || question.question || 'No question text'}
                  </p>
                </div>

                {/* Options */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Options:</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {['A', 'B', 'C', 'D'].map((letter, optIndex) => {
                      const isCorrect = question[`option${letter}`];
                      const optionText = question.optionTexts?.[`option${letter}`] || question.options?.[optIndex] || `Option ${letter}`;

                      return (
                        <div
                          key={letter}
                          className={`flex items-center space-x-2 p-2 rounded-lg border ${isCorrect
                              ? 'border-green-200 bg-green-50'
                              : 'border-gray-200 bg-white'
                            }`}
                        >
                          <span className="font-medium text-gray-700">{letter}.</span>
                          <span className={`flex-1 ${isCorrect ? 'text-green-900 font-medium' : 'text-gray-700'}`}>
                            {optionText}
                          </span>
                          {isCorrect && (
                            <span className="text-green-600 text-sm font-medium">✓ Correct</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Explanation */}
                {question.explanation && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Explanation:</h4>
                    <p className="text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
                      {question.explanation}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {isFromOnline && (
            <div className="flex items-center justify-end space-x-3 pt-6 border-t mt-6">
              <button
                type="button"
                onClick={() => navigate('/home?tab=online')}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { toast.success('Questions saved successfully'); navigate('/home?tab=online') }}
                className="btn-primary"
              >
                Save Questions
              </button>
            </div>
          )}
        </div>
      )}

    </div>
  )
}

export default AddTestQuestions